import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { minuteMs } from "verdi-common/timeConstants";

import { formatTimer } from "verdi-common/utils/timeUtils";

interface UseTimerParams {
    startTime: number; // Time in ms
    timerTickInterval?: number; // in ms
}

export function useTimer({ startTime, timerTickInterval = minuteMs }: UseTimerParams) {
    const { t } = useTranslation(["common"], { keyPrefix: "time" });

    // Validate startTime
    const validStartTime = useMemo(() => {
        const time = Number(startTime);
        if (Number.isNaN(time) || time < 0) {
            console.warn("useTimer: Invalid startTime provided, using current time");
            return Date.now();
        }
        return time;
    }, [startTime]);

    const [elapsedTime, setElapsedTime] = useState(() => Date.now() - validStartTime);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const updateElapsedTime = useCallback(() => {
        setElapsedTime(Date.now() - validStartTime);
    }, [validStartTime]);

    const stopTimer = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const startTimer = useCallback(() => {
        stopTimer();
        intervalRef.current = setInterval(updateElapsedTime, timerTickInterval);
    }, [updateElapsedTime, timerTickInterval, stopTimer]);

    // Start timer and update time when startTime changes
    useEffect(() => {
        updateElapsedTime();
        startTimer();
        return stopTimer;
    }, [updateElapsedTime, startTimer, stopTimer]);

    const elapsedTimeString = useMemo(() => formatTimer(elapsedTime, t("day.char")), [elapsedTime, t]);

    return {
        elapsedTime,
        elapsedTimeString,
        elapsedTimeStringWithSuffix: t("time-ago", { timeAgo: elapsedTimeString }),
        stopTimer,
        startTimer,
        isTicking: intervalRef.current !== null,
    };
}
